import { actionTypes } from "../types/actionTypes"
import type { Action } from "../types/Action"
import type { State } from "../types/State"
import { UI_ACTIONS } from '@shared/constants'

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 1000000

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    // Import dispatch here to avoid circular dependency
    const { dispatch } = require('./dispatch')
    dispatch({
      type: UI_ACTIONS.TOAST.REMOVE_TOAST,
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)
  
  toastTimeouts.set(toastId, timeout)
}

/**
 * @group Functions
 * @summary <blockquote>Reducer function for managing toast state.</blockquote>
 * @example
 * ```tsx
 * const [state, dispatch] = useReducer(toastReducer, { toasts: [] });
 * ```
 */
export const toastReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case UI_ACTIONS.TOAST.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case UI_ACTIONS.TOAST.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case UI_ACTIONS.TOAST.DISMISS_TOAST: {
      const { toastId } = action
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }

    case UI_ACTIONS.TOAST.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
  
  return state
} 